import { realtime } from '../api/realtime.js';
import { ChatBubble } from './ChatBubble.js';
import { shieldFromPhaser } from './uiShield.js';
import { isTouchDevice } from './isTouchDevice.js';
import { chatHtml } from './chatText.mjs';
import { CANNED_ONLY_ROOMS } from './chatRooms.mjs';

const PALETTE = ['Cześć! {wave}', 'Fajny dom! {house}', 'Ładnie! {love_eyes}', '{heart}', '{grin}', 'Pa pa! {wave}'];
// Game-hall floors get extra board-game phrases on top of the base palette.
const HALL_PALETTE_EXTRA = ['Zagramy? {dice}', 'Dobra partia! {trophy}', 'Jeszcze raz? {repeat}'];
const HALL_FLOOR_ROOMS = new Set(['game_hall:down', 'game_hall:up']);

// True when a DOM text field is focused (so a global Enter shouldn't re-open the chat).
function isTextInputFocused() {
  const ae = typeof document !== 'undefined' ? document.activeElement : null;
  return !!ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
}

// Attach a chat control for one room to a scene that has a local avatar at scene.natalka.
// town → canned palette buttons (always visible); any other room → free-text input that
// is HIDDEN by default and opened with Enter (or by tapping the 💬 chip, for touch). Shows
// the local player's own bubble immediately and broadcasts over the room socket. The local
// bubble is replaced on each new message and otherwise lingers ~16s. Cleans up on shutdown.
export function attachRoomChat(scene, room) {
  const isTown = CANNED_ONLY_ROOMS.has(room);
  const wrap = document.createElement('div');
  wrap.id = 'chat-input-wrap';
  wrap.className = 'room-chat';
  Object.assign(wrap.style, {
    position: 'fixed', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
    // z-index above the mobile shell's joystick/action buttons (both z-index:1500) —
    // the free-text bar (table chat) has no joystick-avoidance positioning like the
    // isTown palette does, so on portrait touch it can visually overlap the joystick;
    // without this the joystick (higher stacking) would intercept taps meant for the
    // input/send button in the overlap region.
    display: 'flex', gap: '6px', zIndex: '1600', flexWrap: 'wrap',
    maxWidth: '620px', justifyContent: 'center',
  });

  // Keep the text field visible above the on-screen keyboard on phones. Only the
  // free-text room input opens a keyboard — the town palette is tap-only, so skip it
  // there (otherwise it gets pinned to the keyboard-safe offset for no reason).
  const vv = window.visualViewport;
  if (vv && !isTown) {
    const reposition = () => {
      const overlap = (window.innerHeight - vv.height - vv.offsetTop);
      wrap.style.bottom = `${Math.max(104, overlap + 16)}px`;
    };
    vv.addEventListener('resize', reposition);
    vv.addEventListener('scroll', reposition);
    // store for cleanup where the wrap is torn down:
    wrap._vvCleanup = () => { vv.removeEventListener('resize', reposition); vv.removeEventListener('scroll', reposition); };
  }

  // Only one local speech bubble at a time — a new message replaces the previous one.
  let localBubble = null;
  const sayLocal = (displayText) => {
    if (!scene.natalka) return;
    localBubble?.destroy?.();
    localBubble = new ChatBubble(scene, scene.natalka, displayText);
  };

  let input = null;
  let chip = null;
  let onKeyDown = null;

  if (isTown) {
    const touch = isTouchDevice();
    const phrases = HALL_FLOOR_ROOMS.has(room) ? [...PALETTE, ...HALL_PALETTE_EXTRA] : PALETTE;
    phrases.forEach((phrase) => {
      const b = document.createElement('button');
      b.innerHTML = chatHtml(phrase);   // the phrase is sent as its token string; icons only on screen
      b.style.cssText = touch
        ? 'padding:5px 9px;font:13px system-ui;cursor:pointer;white-space:nowrap;flex:0 0 auto;border-radius:8px;border:1px solid #ccc;background:#ffffffe6'
        : 'padding:8px 10px;font:16px system-ui;cursor:pointer';
      b.onclick = () => { realtime.chat(`palette:${phrase}`); sayLocal(phrase); };
      wrap.appendChild(b);
    });
    document.body.appendChild(wrap);
    shieldFromPhaser(wrap);

    // On touch, keep the palette clear of the joystick (bottom-left) and the action /
    // cancel buttons (bottom-right). Render it as a single horizontal scroll row:
    // anchored at the very bottom between the corners in landscape, and as a compact
    // ribbon just above the joystick in portrait (where both bottom corners are taken).
    if (touch) {
      const layoutTown = () => {
        const landscape = window.innerWidth > window.innerHeight;
        Object.assign(wrap.style, {
          flexWrap: 'nowrap', overflowX: 'auto', maxWidth: 'none', transform: 'none',
          // Centred with auto margins on the end buttons, not justify-content:center — that
          // pushes an overflowing row's first phrases off the left edge where no scroll reaches.
          width: 'auto', justifyContent: 'flex-start',
          left: landscape ? '170px' : '8px',
          right: landscape ? '130px' : '8px',
          bottom: landscape ? '14px' : '152px',
        });
        wrap.firstElementChild.style.marginLeft = landscape ? 'auto' : '0';
        wrap.lastElementChild.style.marginRight = landscape ? 'auto' : '0';
      };
      layoutTown();
      window.addEventListener('resize', layoutTown);
      wrap._layoutCleanup = () => window.removeEventListener('resize', layoutTown);
    }
  } else {
    input = document.createElement('input');
    input.maxLength = 100;
    input.placeholder = 'napisz...';
    input.style.cssText = 'width:300px;padding:8px;font:16px system-ui';
    const btn = document.createElement('button');
    btn.textContent = 'Wyślij';
    btn.style.cssText = 'padding:8px 12px;font:16px system-ui;cursor:pointer';

    // The free-text bar is hidden until opened. A small chip hints at how to open it and
    // doubles as a tap target on touch devices that have no Enter key.
    wrap.style.display = 'none';
    chip = document.createElement('button');
    chip.className = 'room-chat-chip';
    chip.innerHTML = chatHtml('{chat} ⏎');
    chip.title = 'Naciśnij Enter, aby pisać';
    Object.assign(chip.style, {
      position: 'fixed', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
      zIndex: '1600', padding: '8px 12px', font: '16px system-ui', cursor: 'pointer',
      borderRadius: '999px', border: '1px solid #ccc', background: '#ffffffcc',
    });
    // Phones: a round button in the top-right corner. Bottom-centre it covered the rooms' exit
    // signs, the farm's bottom buttons and modal Zamknij buttons (mobile QA 2026-09-30).
    if (isTouchDevice()) {
      chip.innerHTML = chatHtml('{chat}');
      Object.assign(chip.style, {
        bottom: 'auto', top: '6px', left: 'auto', right: '6px', transform: 'none',
        width: '44px', height: '44px', padding: '0', font: '21px system-ui', borderRadius: '50%',
      });
    }

    const openChat = () => {
      wrap.style.display = 'flex';
      if (chip) chip.style.display = 'none';
      input.focus();
    };
    const closeChat = () => {
      input.value = '';
      input.blur();                 // release focus so the player can walk again
      wrap.style.display = 'none';
      if (chip) chip.style.display = 'block';
    };
    const send = () => {
      const t = input.value.trim();
      if (t) { realtime.chat(t); sayLocal(t); }
      closeChat();
    };

    btn.onclick = send;
    chip.onclick = openChat;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.stopPropagation(); send(); }
      else if (e.key === 'Escape') { e.stopPropagation(); closeChat(); }
    });
    // Global Enter opens the chat (when nothing else is focused and it's not already open).
    onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (wrap.style.display !== 'none') return;   // already open — input handles it
      if (isTextInputFocused()) return;            // typing elsewhere (e.g. friend name)
      e.preventDefault();
      openChat();
    };
    window.addEventListener('keydown', onKeyDown);

    wrap.append(input, btn);
    document.body.appendChild(wrap);
    document.body.appendChild(chip);
    shieldFromPhaser(wrap);
    shieldFromPhaser(chip);
  }

  // Clicking the game (or anywhere outside the bar/chip) closes the input so the player can
  // walk again. Phaser preventDefaults canvas mousedown, which would otherwise stop the
  // input from blurring on its own — so we handle it explicitly in the capture phase.
  const blurOnOutside = (e) => {
    if (!input) return;
    if (wrap.contains(e.target) || (chip && chip.contains(e.target))) return;
    input.value = '';
    input.blur();
    wrap.style.display = 'none';
    if (chip) chip.style.display = 'block';
  };
  window.addEventListener('pointerdown', blurOnOutside, true);

  let detached = false;
  const detach = () => {
    if (detached) return;
    detached = true;
    window.removeEventListener('pointerdown', blurOnOutside, true);
    if (onKeyDown) window.removeEventListener('keydown', onKeyDown);
    localBubble?.destroy?.();
    wrap._vvCleanup?.();
    wrap._layoutCleanup?.();
    wrap.remove();
    chip?.remove();
    if (scene._roomChat === api) scene._roomChat = null;
  };
  scene.events.once('shutdown', detach);

  // Force the whole control out of view (used while seated at a table, when the table's
  // free-text bar takes over) and restore it to its default state afterwards.
  const setHidden = (h) => {
    if (h) {
      wrap.style.display = 'none';
      if (chip) chip.style.display = 'none';
    } else if (isTown) {
      wrap.style.display = 'flex';
    } else if (chip) {
      chip.style.display = 'block';   // free-text room default: chip shown, bar closed
    }
  };

  // Out of view while a window covers the bottom of the screen (Wygląd: the town's palette sat over Gotowe on a
  // phone); the returned function puts back whatever was showing (a seated table keeps its palette hidden).
  const coverFor = () => {
    const was = [wrap.style.display, chip?.style.display];
    setHidden(true);
    return () => { wrap.style.display = was[0]; if (chip) chip.style.display = was[1]; };
  };

  const api = { detach, setHidden, coverFor };
  scene._roomChat ??= api;   // the room's own chat (a table's, attached later, does not take its place)
  return api;
}
